import { parseApiUtcDateTime } from '@core/utils/parseApiUtcDateTime';
import { parseDateOnly, formatDateOnly } from '@core/utils/formatDateOnly';
import { normalizeReportWorkflowStatus } from '@core/reports/utils/reportWorkflow';

/** Days after until used when suggesting a create-form deadline. */
export const REPORT_TAG_DEADLINE_DEFAULT_OFFSET_DAYS = 14;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Formats a UTC API datetime for an HTML datetime-local input (browser local).
 */
export function toDatetimeLocalValue(utcApiString?: string | null): string {
  const date = parseApiUtcDateTime(utcApiString);
  if (!date) return '';

  return (
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` +
    `T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
  );
}

/**
 * Converts a datetime-local value to a naive UTC API string (Y-m-d H:i:s, seconds :00).
 */
export function fromDatetimeLocalValue(localInput?: string | null): string | null {
  if (!localInput?.trim()) return null;

  const trimmed = localInput.trim();
  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!match) return null;

  const [, y, m, d, hh, mm] = match;
  const local = new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(hh),
    Number(mm),
    0,
    0
  );
  if (Number.isNaN(local.getTime())) return null;

  return (
    `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())}` +
    ` ${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}:00`
  );
}

/**
 * Create-form default: until + offset days at local 23:59 (datetime-local string).
 */
export function defaultDeadlineFromUntil(untilDateOnly?: string | null): string {
  const until = parseDateOnly(untilDateOnly);
  if (!until) return '';

  const deadline = new Date(
    until.getFullYear(),
    until.getMonth(),
    until.getDate() + REPORT_TAG_DEADLINE_DEFAULT_OFFSET_DAYS,
    23,
    59,
    0,
    0
  );

  return (
    `${deadline.getFullYear()}-${pad2(deadline.getMonth() + 1)}-${pad2(deadline.getDate())}` +
    `T${pad2(deadline.getHours())}:${pad2(deadline.getMinutes())}`
  );
}

/**
 * True when deadline (UTC API or datetime-local) is on/after until 00:00:00 UTC.
 * Mirrors Laravel after_or_equal:until with APP_TIMEZONE=UTC.
 */
export function isDeadlineOnOrAfterUntil(
  deadlineInput: string | null | undefined,
  untilDateOnly: string | null | undefined
): boolean {
  if (!deadlineInput?.trim() || !untilDateOnly?.trim()) return true;

  const untilIso = untilDateOnly.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(untilIso)) return true;

  const untilStartUtc = new Date(`${untilIso}T00:00:00Z`);
  if (Number.isNaN(untilStartUtc.getTime())) return true;

  let deadlineUtc: Date | null = null;
  if (deadlineInput.includes('T') && !deadlineInput.endsWith('Z') && !deadlineInput.includes('+')) {
    const api = fromDatetimeLocalValue(deadlineInput);
    deadlineUtc = parseApiUtcDateTime(api);
  } else {
    deadlineUtc = parseApiUtcDateTime(deadlineInput);
  }

  if (!deadlineUtc) return true;
  return deadlineUtc.getTime() >= untilStartUtc.getTime();
}

/**
 * Formats a UTC API deadline for display in the browser's local timezone.
 * Example: November 15, 2025, 11:59 PM
 */
export function formatReportTagDeadline(utcApiString?: string | null): string | null {
  const date = parseApiUtcDateTime(utcApiString);
  if (!date) return null;
  return date.toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Matches API ReportTagDeadlineUrgency::URGENT_DAYS. */
export const DEADLINE_URGENT_DAYS = 7;

export type DeadlineUrgency = 'info' | 'warning' | 'danger';

export type DeadlineUrgencyResult = {
  daysUntilDeadline: number | null;
  urgency: DeadlineUrgency | null;
};

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Signed whole calendar days (local) from today to the deadline's local calendar day.
 * Urgency uses the full deadline instant vs now (UTC-parsed).
 */
export function getDeadlineUrgency(
  deadlineUtc?: string | null,
  now: Date = new Date()
): DeadlineUrgencyResult {
  const deadline = parseApiUtcDateTime(deadlineUtc);
  if (!deadline) {
    return { daysUntilDeadline: null, urgency: null };
  }

  const daysUntilDeadline = Math.round(
    (startOfLocalDay(deadline).getTime() - startOfLocalDay(now).getTime()) / 86_400_000
  );

  if (now.getTime() > deadline.getTime()) {
    return { daysUntilDeadline, urgency: 'danger' };
  }

  if (daysUntilDeadline <= DEADLINE_URGENT_DAYS) {
    return { daysUntilDeadline, urgency: 'warning' };
  }

  return { daysUntilDeadline, urgency: 'info' };
}

export function formatDeadlineCountdown(
  deadlineUtc?: string | null,
  now: Date = new Date()
): string | null {
  const { daysUntilDeadline, urgency } = getDeadlineUrgency(deadlineUtc, now);
  if (urgency === null || daysUntilDeadline === null) return null;

  if (urgency === 'danger') {
    const lateBy = Math.abs(daysUntilDeadline);
    if (lateBy === 0) return 'Past deadline · late today';
    return `Past deadline · ${lateBy} day${lateBy === 1 ? '' : 's'} late`;
  }

  if (daysUntilDeadline === 0) return 'Deadline today';
  if (daysUntilDeadline === 1) return 'Due in 1 day';
  return `Due in ${daysUntilDeadline} days`;
}

export function formatPeriodRangeLabel(
  since?: string | null,
  until?: string | null
): string | null {
  const sinceLabel = formatDateOnly(since);
  const untilLabel = formatDateOnly(until);
  if (!sinceLabel && !untilLabel) return null;
  if (sinceLabel && untilLabel) return `${sinceLabel} – ${untilLabel}`;
  return sinceLabel ?? untilLabel;
}

export function formatDeadlineAlertMessage(alert: {
  type: string;
  period_name?: string | null;
  deadline?: string | null;
  days_until_deadline?: number | null;
}): string | null {
  const rawName = alert.period_name?.trim();
  const periodLabel = rawName
    ? `reporting period "${rawName}"`
    : 'this reporting period';
  const deadlineLabel = formatReportTagDeadline(alert.deadline);
  const days = alert.days_until_deadline;

  if (alert.type === 'DEADLINE_PASSED') {
    const lateBy = days == null ? null : Math.abs(days);
    const lateText =
      lateBy == null
        ? 'is past its deadline'
        : lateBy === 0
          ? 'is late (due today)'
          : `is ${lateBy} day${lateBy === 1 ? '' : 's'} late`;
    return deadlineLabel
      ? `Past deadline: ${periodLabel} ${lateText} (was due ${deadlineLabel})`
      : `Past deadline: ${periodLabel} ${lateText}`;
  }

  if (alert.type === 'DEADLINE_URGENT') {
    const dueText =
      days == null
        ? 'deadline is approaching'
        : days === 0
          ? 'is due today'
          : `is due in ${days} day${days === 1 ? '' : 's'}`;
    return deadlineLabel
      ? `Deadline approaching: ${periodLabel} ${dueText} on ${deadlineLabel}`
      : `Deadline approaching: ${periodLabel} ${dueText}`;
  }

  return null;
}

/** Activity / template entries are done for deadline urgency once Submitted. */
export function isActivityEntryDeadlineComplete(status?: string | null): boolean {
  return normalizeReportWorkflowStatus(status) === 'SUBMITTED';
}

/** Department reports are done for deadline urgency once Approved (or FINAL). */
export function isDepartmentReportDeadlineComplete(status?: string | null): boolean {
  const normalized = normalizeReportWorkflowStatus(status);
  return normalized === 'APPROVED' || normalized === 'FINAL';
}

/**
 * Colored lateness strip only when the tag is open, deadline needs attention,
 * and this item is not yet in its done state.
 */
export function shouldShowDeadlineUrgencyBanner(args: {
  tagIsOpen?: boolean;
  deadline?: string | null;
  isComplete: boolean;
  now?: Date;
}): boolean {
  if (args.tagIsOpen === false || args.isComplete) {
    return false;
  }
  const { urgency } = getDeadlineUrgency(args.deadline, args.now);
  return urgency === 'warning' || urgency === 'danger';
}

/** True when checklist period still has unfinished templates or department reports. */
export function periodChecklistHasOutstanding(period: {
  templates?: { outstanding?: number };
  department_reports?: { outstanding?: number };
}): boolean {
  const templatesOutstanding = period.templates?.outstanding ?? 0;
  const departmentOutstanding = period.department_reports?.outstanding ?? 0;
  return templatesOutstanding + departmentOutstanding > 0;
}
